import React, { Component } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { ToastContainer, toast } from "react-toastify";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { siteMasterService } from "../../services/siteMasterService";
import "./SiteMaster.css";

const emptyForm = () => ({
  id: 0,
  siteStartDate: "",
  ownerName: "",
  ownerPinCode: "",
  ownerMobileNo: "",
  address: "",
  city: "",
  state: "",
  architectId: "",
  architectName: "",
  architectMobileNo: "",
  architechPincode: "",
  architechEmail: "",
  applicatorName: "",
  applicatorMobileNo: "",
  applicatorPinCode: "",
  contractorId: "",
  contractorName: "",
  contractorMobileNo: "",
  contractorEmail: "",
  contractorPincode: "",
  carpenterId: "",
  carpenterName: "",
  carpenterMobileNo: "",
  carpenterEmail: "",
  status: "1",
  month: new Date().toLocaleString("en-US", { month: "long" }),
  createdBy: getCreatedBy(),
});

function getCreatedBy() {
  const storedUser = sessionStorage.getItem("UserData");
  if (!storedUser) return "";

  try {
    const parsedUser = JSON.parse(storedUser);
    return String(
      parsedUser && typeof parsedUser === "object"
        ? parsedUser.UserId || parsedUser.Id || ""
        : parsedUser || ""
    );
  } catch (error) {
    return storedUser;
  }
}

class SiteMaster extends Component {
  state = {
    rows: [],
    statuses: [],
    stateOptions: [],
    cityOptions: [],
    architectSuggestions: [],
    contractorSuggestions: [],
    carpenterSuggestions: [],
    first: 0,
    pageSize: 10,
    pageNo: 1,
    totalRecords: 0,
    searchValue: "",
    sortBy: "is",
    orderBy: "desc",
    loading: false,
    dialogVisible: false,
    form: emptyForm(),
    error: "",
    statusError: "",
    saving: false,
  };

  componentDidMount() {
    this.loadSites();
    this.loadStatuses();
    this.loadStateOptions();
    this.loadCityOptions(1);
  }

  loadStatuses = async () => {
    try {
      const response = await siteMasterService.getSiteStatus();
      if (!response || Number(response.ResponseCode) !== 200) {
        throw new Error(response?.ResponseMessage || "Unable to load site statuses.");
      }
      this.setState({
        statuses: (response.ResponseData || []).map((status) => ({
          value: String(status.Value),
          label: status.Label,
        })),
      });
    } catch (error) {
      const message = error.message || String(error);
      this.setState({ statusError: message });
      toast.error(message);
    }
  };

  loadStateOptions = async () => {
    try {
      const response = await siteMasterService.getStateDDL(1);
      if (!response || Number(response.ResponseCode) !== 200) {
        throw new Error(response?.ResponseMessage || "Unable to load states.");
      }

      this.setState({
        stateOptions: (response.ResponseData || []).map((state) => ({
          value: String(state.value ?? state.Value ?? ""),
          label: state.label ?? state.Label ?? "",
        })),
      });
    } catch (error) {
      const message = error.message || String(error);
      this.setState({ statusError: message });
      toast.error(message);
    }
  };

  loadCityOptions = async (stateId = 1) => {
    const currentState = Number(stateId) || 1;

    try {
      const response = await siteMasterService.getCityDDL(currentState);
      if (!response || Number(response.ResponseCode) !== 200) {
        throw new Error(response?.ResponseMessage || "Unable to load cities.");
      }

      this.setState({
        cityOptions: (response.ResponseData || []).map((city) => ({
          value: String(city.value ?? city.Value ?? ""),
          label: city.label ?? city.Label ?? "",
        })),
      });
    } catch (error) {
      const message = error.message || String(error);
      this.setState({ statusError: message });
      toast.error(message);
    }
  };

  loadSites = async (overrides = {}) => {
    const nextState = { ...this.state, ...overrides };
    this.setState({ loading: true, error: "", ...overrides });

    try {
      const response = await siteMasterService.getAdminSiteDetails({
        searchValue: nextState.searchValue,
        pageNo: nextState.pageNo,
        pageSize: nextState.pageSize,
        sortBy: nextState.sortBy,
        orderBy: nextState.orderBy,
      });
      if (!response || response.ResponseCode !== "200") {
        throw new Error(response?.ResponseMessage || "Unable to load sites.");
      }

      const rows = response.ResponseData || [];
      this.setState({
        rows,
        totalRecords: rows.length ? Number(rows[0].TotalRows) || 0 : 0,
        loading: false,
      });
    } catch (error) {
      const message = error.message || String(error);
      this.setState({ loading: false, error: message });
      toast.error(message);
    }
  };

  openCreateDialog = () => {
    this.setState({ form: emptyForm(), dialogVisible: true, error: "" });
  };

  openEditDialog = async (row) => {
    const selectedState = row.State ? String(row.State) : "1";
    const selectedCity = row.City ? String(row.City) : "";

    this.setState({
      form: {
        ...emptyForm(),
        id: row.SiteId || 0,
        siteStartDate: row.SiteStartDate ? row.SiteStartDate.slice(0, 16) : "",
        ownerName: row.OwnerName || "",
        ownerPinCode: row.OwnerPinCode || "",
        ownerMobileNo: row.OwnerMobileNo || "",
        address: row.Address || "",
        city: selectedCity,
        state: selectedState,
        architectId: row.ArchitectId ? String(row.ArchitectId) : "",
        architectName: row.ArchitectName || "",
        architectMobileNo: row.ArchitectMobileNo || "",
        architechPincode: row.ArchitechPincode || row.ArchitectPinCode || "",
        architechEmail: row.ArchitectEmail || "",
        applicatorName: row.ApplicatorName || "",
        applicatorMobileNo: row.ApplicatorMobileNo || "",
        applicatorPinCode: row.ApplicatorPinCode || "",
        contractorId: row.ContractorId ? String(row.ContractorId) : "",
        contractorName: row.ContractorName || "",
        contractorMobileNo: row.ContractorMobileNo || "",
        contractorEmail: row.ContractorEmail || "",
        contractorPincode: row.ContractorPincode || "",
        carpenterId: row.CarpenterId != null ? String(row.CarpenterId) : "",
        carpenterName: row.CarpenterName || "",
        carpenterMobileNo: row.CarpenterMobileNo || "",
        carpenterEmail: row.CarpenterEmail || "",
        status: String(row.StatusId || 1),
        month: row.Month || "",
      },
      dialogVisible: true,
      error: "",
    });

    await this.loadCityOptions(selectedState);
  };

  closeDialog = () => {
    this.setState({ dialogVisible: false, error: "" });
  };

  handleInputChange = (event) => {
    const { name, value } = event.target;
    this.setState((state) => ({
      form: { ...state.form, [name]: value },
      error: "",
    }));
  };

  bindArchitectSelection = (architect) => {
    if (!architect) return;

    this.setState((state) => ({
      form: {
        ...state.form,
        architectId: architect.ArchitectId != null ? String(architect.ArchitectId) : state.form.architectId,
        architectName: architect.ArchitectId != null ? String(architect.ArchitectId) : state.form.architectName,
        architectMobileNo: architect.MobileNo || state.form.architectMobileNo,
        architechEmail: architect.Email || state.form.architechEmail,
        architechPincode: architect.Pincode || state.form.architechPincode,
      },
      architectSuggestions: [],
    }));
  };

  bindContractorSelection = (contractor) => {
    if (!contractor) return;

    this.setState((state) => ({
      form: {
        ...state.form,
        contractorId: contractor.ContractorId != null ? String(contractor.ContractorId) : state.form.contractorId,
        contractorName: contractor.UserName || state.form.contractorName,
        contractorMobileNo: contractor.MobileNo || state.form.contractorMobileNo,
        contractorEmail: contractor.Email || state.form.contractorEmail,
        contractorPincode: contractor.Pincode || state.form.contractorPincode,
      },
      contractorSuggestions: [],
    }));
  };

  bindCarpenterSelection = (carpenter) => {
    if (!carpenter) return;

    const carpenterId = carpenter.CarpenterId ?? carpenter.ArchitectId;
    this.setState((state) => ({
      form: {
        ...state.form,
        carpenterId: carpenterId != null ? String(carpenterId) : state.form.carpenterId,
        carpenterName: carpenter.UserName || state.form.carpenterName,
        carpenterMobileNo: carpenter.MobileNo || state.form.carpenterMobileNo,
        carpenterEmail: carpenter.Email || state.form.carpenterEmail,
      },
      carpenterSuggestions: [],
    }));
  };

  handleArchitectMobileChange = async (value) => {
    const mobileNo = value || "";
    this.setState((state) => ({
      form: { ...state.form, architectMobileNo: mobileNo },
      error: "",
    }));

    if (!mobileNo || mobileNo.trim().length < 3) {
      this.setState({ architectSuggestions: [] });
      return;
    }

    try {
      const response = await siteMasterService.getArchitectDetailsByMobile({ mobileNo: mobileNo.trim() });
      const details = response?.ResponseData || [];
      this.setState({
        architectSuggestions: details.map((person) => ({
          ...person,
          label: `${person.UserName || ""} ${person.MobileNo || ""}`.trim(),
        })),
      });
    } catch (error) {
      const message = error.message || String(error);
      toast.error(message);
    }
  };

  handleContractorMobileChange = async (value) => {
    const mobileNo = value || "";
    this.setState((state) => ({
      form: { ...state.form, contractorMobileNo: mobileNo },
      error: "",
    }));

    if (!mobileNo || mobileNo.trim().length < 3) {
      this.setState({ contractorSuggestions: [] });
      return;
    }

    try {
      const response = await siteMasterService.getContractorDetails({ mobileNo: mobileNo.trim() });
      const details = response?.ResponseData || [];
      this.setState({
        contractorSuggestions: details.map((person) => ({
          ...person,
          label: `${person.UserName || ""} ${person.MobileNo || ""}`.trim(),
        })),
      });
    } catch (error) {
      const message = error.message || String(error);
      toast.error(message);
    }
  };

  handleCarpenterMobileChange = async (value) => {
    const mobileNo = value || "";
    this.setState((state) => ({
      form: {
        ...state.form,
        carpenterMobileNo: mobileNo,
        carpenterId: "",
      },
      error: "",
    }));

    if (!mobileNo || mobileNo.trim().length < 3) {
      this.setState({ carpenterSuggestions: [] });
      return;
    }

    try {
      const response = await siteMasterService.getCarpenterDetailsByMobile({ mobileNo: mobileNo.trim() });
      const details = response?.ResponseData || [];
      this.setState({
        carpenterSuggestions: details.map((person) => ({
          ...person,
          label: `${person.UserName || ""} ${person.MobileNo || ""}`.trim(),
        })),
      });
    } catch (error) {
      const message = error.message || String(error);
      toast.error(message);
    }
  };

  handleStateChange = async (event) => {
    const stateValue = event.target.value;
    this.setState((currentState) => ({
      form: { ...currentState.form, state: stateValue, city: "" },
      error: "",
    }));
    await this.loadCityOptions(stateValue || 1);
  };

  submitForm = async (event) => {
    event.preventDefault();
    const { form } = this.state;
    if (!form.siteStartDate || !form.ownerName.trim() || !form.address.trim()) {
      const message = "Start date, owner name, and address are required.";
      this.setState({ error: message });
      toast.error(message);
      return;
    }

    const payload = {
      id: Number(form.id) || 0,
      siteStartDate: form.siteStartDate.length === 16 ? `${form.siteStartDate}:00` : form.siteStartDate,
      ownerName: form.ownerName,
      ownerPinCode: form.ownerPinCode,
      ownerMobileNo: form.ownerMobileNo,
      address: form.address,
      city: form.city,
      state: form.state,
      architectName: Number(form.architectId) || 0,
      architectMobileNo: form.architectMobileNo,
      architechPincode: form.architechPincode,
      architechEmail: form.architechEmail,
      applicatorName: form.applicatorName,
      applicatorMobileNo: form.applicatorMobileNo,
      applicatorPinCode: form.applicatorPinCode,
      contractorId: Number(form.contractorId) || 0,
      carpenterName: form.carpenterName,
      carpenterMobileNo: form.carpenterMobileNo,
      carpenterId: Number(form.carpenterId) || 0,
      status: Number(form.status),
      month: form.month,
      createdBy: form.createdBy,
    };

    this.setState({ saving: true, error: "" });
    try {
      const response = await siteMasterService.createSite(payload);
      if (!response || response.ResponseCode !== "200") {
        throw new Error(response?.ResponseMessage || "Unable to save site.");
      }

      this.setState({ dialogVisible: false, saving: false, first: 0, pageNo: 1 });
      toast.success(form.id ? "Site updated successfully." : "Site created successfully.");
      this.loadSites({ first: 0, pageNo: 1 });
    } catch (error) {
      const message = error.message || String(error);
      this.setState({ saving: false, error: message });
      toast.error(message);
    }
  };

  renderAction = (row) => (
    <Button
      type="button"
      icon="pi pi-pencil"
      className="p-button-text p-button-sm"
      aria-label={`Edit site `}
      tooltip="Edit site"
      onClick={() => this.openEditDialog(row)}
    />
  );

  renderStatus = (row) =>
    row.StatusName ||
    this.state.statuses.find((status) => Number(status.value) === Number(row.StatusId))?.label ||
    "-";

  renderTableHeader = () => (
    <div className="table-header row site-master-table-header">
      <div className="col-md-6 master_headhingcol">
        <h4>Site Master</h4>
      </div>
      <div className="col-md-6 text-right site-master-table-actions">
        <form
          className="site-master-search"
          onSubmit={(event) => {
            event.preventDefault();
            this.loadSites({ first: 0, pageNo: 1 });
          }}
        >
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              aria-label="Search sites"
              placeholder="Search sites"
              value={this.state.searchValue}
              onChange={(event) => this.setState({ searchValue: event.target.value })}
            />
          </span>
          <Button type="submit" icon="pi pi-search" aria-label="Search sites" />
        </form>
        {/* <Button
          type="button"
          label="Add Site"
          icon="pi pi-plus-circle"
          onClick={this.openCreateDialog}
        /> */}
      </div>
    </div>
  );

  renderSection(title, icon, fields) {
    return (
      <section className="site-master-section" key={title}>
        <h5 className="site-master-section-title">
          <i className={`pi ${icon}`} /> {title}
        </h5>
        <div className="row">{fields}</div>
      </section>
    );
  }

  renderField(label, name, options = {}) {
    const { form } = this.state;
    const col = options.full ? "col-12" : "col-lg-4 col-md-6 col-12";
    return (
      <div className={`${col} site-master-field`} key={name}>
        <TextField
          name={name}
          value={form[name]}
          onChange={options.onChange || (options.readOnly ? undefined : this.handleInputChange)}
          type={options.type || "text"}
          required={options.required || false}
          inputProps={{
            maxLength: options.maxLength,
            readOnly: options.readOnly || false,
          }}
          InputProps={{ readOnly: options.readOnly || false }}
          InputLabelProps={options.type === "datetime-local" ? { shrink: true } : undefined}
          multiline={options.multiline || false}
          rows={options.multiline ? 2 : undefined}
          size="small"
          variant="outlined"
          fullWidth
          label={label}
        />
      </div>
    );
  }

  renderAutocompleteField(label, name, options = {}) {
    const { form } = this.state;
    const col = options.full ? "col-12" : "col-lg-4 col-md-6 col-12";
    const suggestions = options.suggestions || [];
    const value = form[name] || "";

    return (
      <div className={`${col} site-master-field`} key={name}>
        <Autocomplete
          freeSolo
          options={suggestions}
          value={value}
          inputValue={value}
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            return option?.label || option?.UserName || option?.MobileNo || "";
          }}
          onInputChange={(event, newInputValue) => {
            if (event && typeof event.preventDefault === "function") {
              event.preventDefault();
            }
            this.setState((state) => ({
              form: { ...state.form, [name]: newInputValue },
              error: "",
            }));
            if (options.onInputChange) {
              options.onInputChange(newInputValue);
            }
          }}
          onChange={(event, newValue) => {
            if (newValue && typeof newValue === "object" && options.onSelect) {
              options.onSelect(newValue);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{
                ...params.inputProps,
                maxLength: options.maxLength,
              }}
            />
          )}
        />
      </div>
    );
  }

  renderSelectField(label, name, options = []) {
    const { form } = this.state;
    const col = options.full ? "col-12" : "col-lg-4 col-md-6 col-12";
    return (
      <div className={`${col} site-master-field`} key={name}>
        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel id={`${name}-label`}>{label}</InputLabel>
          <Select
            labelId={`${name}-label`}
            id={name}
            name={name}
            value={form[name] ?? ""}
            onChange={name === "state" ? this.handleStateChange : this.handleInputChange}
            label={label}
          >
            <MenuItem value=""><em>Select {label}</em></MenuItem>
            {options.map((option) => (
              <MenuItem key={`${name}-${option.value}`} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    );
  }

  render() {
    const { form } = this.state;
    return (
      <main className="site-master page-wrapper">
        <ToastContainer position="top-right" autoClose={3000} />
        {this.state.error && !this.state.dialogVisible && (
          <div className="site-master-error" role="alert">{this.state.error}</div>
        )}

        <div className="card">
          <DataTable
            value={this.state.rows}
            dataKey="SiteId"
            lazy
            paginator
            first={this.state.first}
            rows={this.state.pageSize}
            totalRecords={this.state.totalRecords}
            loading={this.state.loading}
            sortField={this.state.sortBy}
            sortOrder={this.state.orderBy === "asc" ? 1 : -1}
            onPage={(event) => this.loadSites({
              first: event.first,
              pageSize: event.rows,
              pageNo: Math.floor(event.first / event.rows) + 1,
            })}
            onSort={(event) => this.loadSites({
              first: 0,
              pageNo: 1,
              sortBy: event.sortField || "is",
              orderBy: event.sortOrder === 1 ? "asc" : "desc",
            })}
            rowsPerPageOptions={[10, 25, 50]}
            emptyMessage="No sites found."
            header={this.renderTableHeader()}
            responsiveLayout="scroll"
          >
            <Column field="SiteStartDate" header="Start Date" sortable />
            <Column field="OwnerName" header="Owner" sortable />
            <Column field="OwnerMobileNo" header="Owner Mobile" />
            <Column field="Address" header="Address" />
            <Column field="CityName" header="City" sortable />
            <Column field="StateName" header="State" />
            <Column field="ArchitectName" header="Architect" />
            <Column field="ApplicatorName" header="Applicator" />
            <Column field="ContractorName" header="Contractor" />
            <Column field="Month" header="Month" />
            <Column header="Status" body={this.renderStatus} />
            <Column header="Actions" body={this.renderAction} style={{ width: "5rem" }} />
          </DataTable>
        </div>

        <Dialog
          header={form.id ? `Edit Site ` : "Add Site"}
          visible={this.state.dialogVisible}
          onHide={this.closeDialog}
          modal
          dismissableMask
          className="popup site-master-dialog"
        >
          <form onSubmit={this.submitForm} autoComplete="off">
            {this.renderSection("Site Details", "pi-map-marker", [
              this.renderField("Site start date", "siteStartDate", { type: "datetime-local", required: true }),
              this.renderField("Month", "month", { maxLength: 20 }),
              <div className="col-lg-4 col-md-6 col-12 site-master-field" key="status">
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="site-master-status-label">Status</InputLabel>
                  <Select
                    labelId="site-master-status-label"
                    id="site-master-status"
                    name="status"
                    value={form.status}
                    onChange={this.handleInputChange}
                    label="Status"
                  >
                    {this.state.statuses.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>,
              this.renderField("Address", "address", { required: true, maxLength: 300, full: true, multiline: true }),
              this.renderSelectField("State", "state", this.state.stateOptions),
              this.renderSelectField("City", "city", this.state.cityOptions),
            ])}

            {this.renderSection("Owner", "pi-user", [
              this.renderField("Owner name", "ownerName", { required: true, maxLength: 120 }),
              this.renderField("Owner mobile", "ownerMobileNo", { maxLength: 20 }),
              this.renderField("Owner PIN code", "ownerPinCode", { maxLength: 10 }),
            ])}

            {this.renderSection("Architect", "pi-briefcase", [
              this.renderAutocompleteField("Architect mobile", "architectMobileNo", {
                maxLength: 20,
                suggestions: this.state.architectSuggestions,
                onInputChange: this.handleArchitectMobileChange,
                onSelect: this.bindArchitectSelection,
              }),
              this.renderField("Architect Name", "architectName"),
              this.renderField("Architect PIN code", "architechPincode", { maxLength: 10 }),
              this.renderField("Architect email", "architechEmail", { type: "email", maxLength: 150 }),
            ])}

            {this.renderSection("Applicator & Contractor", "pi-users", [
              this.renderAutocompleteField("Contractor mobile", "contractorMobileNo", {
                maxLength: 20,
                suggestions: this.state.contractorSuggestions,
                onInputChange: this.handleContractorMobileChange,
                onSelect: this.bindContractorSelection,
              }),
              this.renderField("Contractor Name", "contractorName"),
              this.renderField("Contractor email", "contractorEmail", { type: "email", maxLength: 150 }),
              this.renderField("Contractor PIN code", "applicatorPinCode", { maxLength: 10 }),
            ])}

            {this.renderSection("Carpenter", "pi-user", [
              this.renderAutocompleteField("Carpenter Mobile", "carpenterMobileNo", {
                maxLength: 20,
                suggestions: this.state.carpenterSuggestions,
                onInputChange: this.handleCarpenterMobileChange,
                onSelect: this.bindCarpenterSelection,
              }),
              this.renderField("Carpenter Name", "carpenterName"),
            ])}

            {this.state.statusError && (
              <div className="site-master-error" role="alert">{this.state.statusError}</div>
            )}
            {this.state.error && (
              <div className="site-master-error" role="alert">{this.state.error}</div>
            )}

            <div className="site-master-dialog-actions">
              <span className="site-master-required-note">
                Fields marked with (*) are required
              </span>
              <div className="site-master-dialog-buttons">
                <Button
                  type="button"
                  label="Cancel"
                  className="p-button-text p-button-secondary"
                  onClick={this.closeDialog}
                />
                <Button
                  type="submit"
                  label={this.state.saving ? "Saving..." : "Save Site"}
                  icon="pi pi-check"
                  className="p-button-success"
                  disabled={this.state.saving}
                />
              </div>
            </div>
          </form>
        </Dialog>
      </main>
    );
  }
}

export default SiteMaster;